import { useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { reportMoney, reportMonthToDate, reportMonthWindow, reportNumber, runReport, type ReportResult } from '../lib/reports';
import { dayLabel, eachDate, monthLabel } from './useControlCentreReports';

export interface SalesDay {
  date: string;
  label: string;
  total: number;
  orders: number;
  isToday: boolean;
}

export interface SalesShare {
  key: string;
  label: string;
  amount: number;
  count: number;
  /** Share of the month's sales, 0 to 1. */
  share: number;
}

export interface SalesReportDetails {
  loading: boolean;
  error: string | null;
  monthLabel: string;
  totalSales: number;
  lastMonth: number;
  orders: number;
  averageSale: number;
  today: number;
  avgPerDay: number;
  projected: number;
  grossSales: number;
  discounts: number;
  tax: number;
  totalInvoiced: number;
  paid: number;
  owing: number;
  tips: number;
  surcharges: number;
  customers: number;
  newCustomers: number;
  returningCustomers: number;
  days: SalesDay[];
  sources: SalesShare[];
  customerTypes: SalesShare[];
  services: SalesShare[];
  teamMembers: SalesShare[];
  saleTypes: SalesShare[];
}

const empty: SalesReportDetails = {
  loading: true,
  error: null,
  monthLabel: '',
  totalSales: 0,
  lastMonth: 0,
  orders: 0,
  averageSale: 0,
  today: 0,
  avgPerDay: 0,
  projected: 0,
  grossSales: 0,
  discounts: 0,
  tax: 0,
  totalInvoiced: 0,
  paid: 0,
  owing: 0,
  tips: 0,
  surcharges: 0,
  customers: 0,
  newCustomers: 0,
  returningCustomers: 0,
  days: [],
  sources: [],
  customerTypes: [],
  services: [],
  teamMembers: [],
  saleTypes: [],
};

const LABELS: Record<string, string> = {
  new: 'New customers',
  returning: 'Repeat customers',
  unknown: 'No customer on file',
  booking: 'Jobs',
  order: 'Online orders',
  counter_sale: 'Counter sales',
  proposal: 'Proposals',
  rental: 'Rentals',
  subscription: 'Subscriptions',
  manual: 'Manual invoices',
};

function prettify(key: string): string {
  return LABELS[key] || key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function shares(result: ReportResult, dimension: string, total: number, limit = 100): SalesShare[] {
  return result.rows
    .map((row) => {
      const key = String(row[dimension] ?? '');
      const amount = reportMoney({ ...result, rows: [row] }, 'total_sales');
      return {
        key,
        label: prettify(key),
        amount,
        count: reportNumber({ ...result, rows: [row] }, 'orders'),
        share: total > 0 ? amount / total : 0,
      };
    })
    .filter((row) => row.amount > 0 || row.count > 0)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, limit);
}

/**
 * Everything behind the Sales card's expanded view, from the sales report.
 * Loads when `open` flips to true and again each time the modal reopens.
 */
export function useSalesReportDetails(open: boolean): SalesReportDetails {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timeZone = selectedBusiness?.timezone || 'America/Vancouver';
  const [details, setDetails] = useState<SalesReportDetails>(empty);

  useEffect(() => {
    if (!open || !businessId) return;
    let cancelled = false;

    const mtd = reportMonthToDate(timeZone, 0);
    const lastMtd = reportMonthToDate(timeZone, -1);
    const today = mtd.until;
    const daysElapsed = Number(today.slice(8, 10));
    const daysInMonth = Number(reportMonthWindow(timeZone, 0).until.slice(8, 10));
    const window = `SINCE ${mtd.since} UNTIL ${mtd.until}`;

    setDetails((current) => ({ ...current, loading: current.days.length === 0, error: null }));
    void Promise.all([
      runReport(
        businessId,
        `FROM sales SHOW total_sales, orders, average_sale, gross_sales, discounts, tax, total_invoiced, paid, owing, tips, surcharges, customers, new_customers, returning_customers ${window}`,
      ),
      runReport(businessId, `FROM sales SHOW total_sales SINCE ${lastMtd.since} UNTIL ${lastMtd.until}`),
      runReport(businessId, `FROM sales SHOW total_sales, orders GROUP BY day ${window}`),
      runReport(businessId, `FROM sales SHOW total_sales, orders GROUP BY source ${window}`),
      runReport(businessId, `FROM sales SHOW total_sales, orders GROUP BY new_or_returning_customer ${window}`),
      runReport(businessId, `FROM sales SHOW total_sales, orders GROUP BY service ${window}`),
      runReport(businessId, `FROM sales SHOW total_sales, orders GROUP BY team_member ${window}`),
      runReport(businessId, `FROM sales SHOW total_sales, orders GROUP BY sale_type ${window}`),
    ])
      .then(([totals, previous, byDay, bySource, byCustomerType, byService, byTeam, bySaleType]) => {
        if (cancelled) return;
        const totalSales = reportMoney(totals);
        const dayRows = new Map(byDay.rows.map((row) => [String(row.day), row]));
        const days = eachDate(mtd.since, today).map((date) => {
          const row = dayRows.get(date);
          return {
            date,
            label: dayLabel(date),
            total: row ? reportMoney({ ...byDay, rows: [row] }, 'total_sales') : 0,
            orders: row ? reportNumber({ ...byDay, rows: [row] }, 'orders') : 0,
            isToday: date === today,
          };
        });
        const avgPerDay = daysElapsed > 0 ? totalSales / daysElapsed : 0;
        setDetails({
          loading: false,
          error: null,
          monthLabel: monthLabel(mtd.since),
          totalSales,
          lastMonth: reportMoney(previous),
          orders: reportNumber(totals, 'orders'),
          averageSale: reportMoney(totals, 'average_sale'),
          today: days.find((day) => day.isToday)?.total || 0,
          avgPerDay,
          projected: avgPerDay * daysInMonth,
          grossSales: reportMoney(totals, 'gross_sales'),
          discounts: reportMoney(totals, 'discounts'),
          tax: reportMoney(totals, 'tax'),
          totalInvoiced: reportMoney(totals, 'total_invoiced'),
          paid: reportMoney(totals, 'paid'),
          owing: reportMoney(totals, 'owing'),
          tips: reportMoney(totals, 'tips'),
          surcharges: reportMoney(totals, 'surcharges'),
          customers: reportNumber(totals, 'customers'),
          newCustomers: reportNumber(totals, 'new_customers'),
          returningCustomers: reportNumber(totals, 'returning_customers'),
          days,
          sources: shares(bySource, 'source', totalSales, 8),
          customerTypes: shares(byCustomerType, 'new_or_returning_customer', totalSales),
          services: shares(byService, 'service', totalSales, 6),
          teamMembers: shares(byTeam, 'team_member', totalSales, 8),
          saleTypes: shares(bySaleType, 'sale_type', totalSales),
        });
      })
      .catch((err: unknown) => {
        console.error('Sales report details:', err);
        if (cancelled) return;
        setDetails({
          ...empty,
          loading: false,
          error: err instanceof Error ? err.message : 'Could not load sales',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [open, businessId, timeZone]);

  return details;
}
